<template>
  <div data-testid="on-upload-pipeline" class="on-upload-pipeline">
    <p class="text-body-2 mb-3">{{ $t('settings_project.upload_pipeline_intro') }}</p>

    <div
      v-if="steps.length === 0"
      class="text-medium-emphasis text-body-2 mb-3"
      data-testid="on-upload-pipeline-empty"
    >
      {{ $t('settings_project.upload_pipeline_empty') }}
    </div>

    <div
      v-for="(step, index) in steps"
      :key="step._key"
      class="pipeline-step mb-3 pa-3"
      :class="{ 'pipeline-step--dragging': dragFrom === index }"
      :data-testid="'on-upload-step-' + index"
      @dragover.prevent="onDragOver(index)"
      @drop.prevent="onDrop(index)"
    >
      <div class="d-flex align-center mb-2" style="gap: 8px;">
        <span
          class="pipeline-drag-handle"
          draggable="true"
          data-testid="on-upload-step-drag-handle"
          :title="$t('settings_project.upload_pipeline_drag_hint')"
          @dragstart="onDragStart(index, $event)"
          @dragend="onDragEnd"
        >
          <v-icon size="small">fas fa-grip-vertical</v-icon>
        </span>
        <span class="text-caption text-medium-emphasis">
          {{ $t('settings_project.upload_pipeline_step_n', { n: index + 1 }) }}
        </span>
        <v-chip size="x-small" variant="tonal" class="ml-1">
          {{ stepTypeLabel(step.type) }}
        </v-chip>
        <v-spacer />
        <v-btn
          icon
          variant="text"
          color="error"
          size="small"
          data-testid="on-upload-step-remove"
          :aria-label="$t('settings_project.upload_pipeline_remove')"
          @click="removeStep(index)"
        >
          <v-icon size="small">fas fa-trash</v-icon>
        </v-btn>
      </div>

      <template v-if="step.type === 'model'">
        <v-autocomplete
          v-model="step.model_id"
          :items="modelSelectItems"
          item-title="title"
          item-value="value"
          :label="$t('settings_project.upload_pipeline_model_label')"
          :placeholder="$t('settings_project.upload_pipeline_model_search')"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          clearable
          :disabled="disabled || modelsLoading"
          data-testid="on-upload-step-model-select"
          @update:model-value="emitSteps"
        >
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              :title="item.raw.title"
              :subtitle="item.raw.subtitle || undefined"
              data-testid="on-upload-step-model-option"
            />
          </template>
        </v-autocomplete>

        <v-switch
          v-model="step.run_on_crop"
          class="mt-3"
          inset
          hide-details
          dense
          color="primary"
          :disabled="disabled"
          :label="$t('settings_project.upload_pipeline_run_on_crop')"
          data-testid="on-upload-step-run-on-crop"
          @update:model-value="(v) => onRunOnCropToggle(step, v)"
        />
        <div
          v-if="step.run_on_crop"
          class="mt-2"
          data-testid="on-upload-step-crop-label"
        >
          <search-label-autocomplete
            :label_type="1"
            :field_label="$t('settings_project.upload_pipeline_crop_label')"
            :placeholder="$t('settings_project.upload_pipeline_crop_label_placeholder')"
            :value="step._cropLabelObj"
            :disabled="disabled"
            @label-selected="(label) => onCropLabelSelect(step, label)"
            @cleared="() => onCropLabelCleared(step)"
          />
          <p class="text-caption mt-1 mb-0">
            {{ $t('settings_project.upload_pipeline_crop_hint') }}
          </p>
        </div>
      </template>

      <template v-else-if="step.type === 'all_active'">
        <p class="text-body-2 mb-0">{{ $t('settings_project.upload_pipeline_all_active_hint') }}</p>
      </template>

      <template v-else-if="step.type === 'llm'">
        <div class="d-flex flex-wrap align-center mb-2" style="gap: 4px 16px;">
          <v-switch
            v-model="step.do_find"
            class="mt-0"
            inset
            hide-details
            dense
            color="primary"
            :disabled="disabled"
            :label="$t('llm_analyse.task_find')"
            @update:model-value="(v) => onLlmTaskToggle(step, 'do_find', v)"
          />
          <v-switch
            v-model="step.do_verify"
            class="mt-0"
            inset
            hide-details
            dense
            color="primary"
            :disabled="disabled"
            :label="$t('llm_analyse.task_verify')"
            @update:model-value="(v) => onLlmTaskToggle(step, 'do_verify', v)"
          />
          <v-switch
            v-model="step.do_llm_question"
            class="mt-0"
            inset
            hide-details
            dense
            color="primary"
            :disabled="disabled"
            :label="$t('llm_analyse.task_llm_question')"
            @update:model-value="(v) => onLlmTaskToggle(step, 'do_llm_question', v)"
          />
          <v-switch
            v-model="step.do_llm_prompt_boxes"
            class="mt-0"
            inset
            hide-details
            dense
            color="primary"
            :disabled="disabled"
            :label="$t('llm_analyse.task_llm_prompt_boxes')"
            @update:model-value="(v) => onLlmTaskToggle(step, 'do_llm_prompt_boxes', v)"
          />
        </div>

        <div v-if="step.do_find">
          <p class="text-caption mb-2 font-weight-medium">{{ $t('llm_analyse.find_section_title') }}</p>
          <v-combobox
            v-model="step._subjectChips"
            outlined
            dense
            chips
            small-chips
            clearable
            multiple
            :disabled="disabled"
            :label="$t('llm_analyse.subjects_label')"
            :hint="$t('llm_analyse.subjects_hint')"
            persistent-hint
            :delimiters="[',']"
            :placeholder="$t('llm_analyse.subjects_placeholder')"
            data-testid="on-upload-step-llm-subjects"
            @update:model-value="syncFindQuery(step)"
          />
        </div>

        <div v-if="step.do_verify" class="mt-3">
          <p class="text-caption mb-2 font-weight-medium">{{ $t('llm_analyse.verify_section_title') }}</p>
          <search-label-autocomplete
            :label_type="1"
            :field_label="$t('llm_analyse.verify_category_label')"
            :placeholder="$t('llm_analyse.verify_category_placeholder')"
            :value="step._verifyCategoryObj"
            @label-selected="(label) => onVerifySelect(step, label)"
            @cleared="() => onVerifyCleared(step)"
          />
          <p class="text-caption mt-2 mb-0">{{ $t('llm_analyse.verify_hint') }}</p>
        </div>

        <div v-if="step.do_llm_question" class="mt-3">
          <p class="text-caption mb-2 font-weight-medium">{{ $t('llm_analyse.question_section_title') }}</p>
          <v-text-field
            v-model="step.llm_question"
            outlined
            dense
            :disabled="disabled"
            :label="$t('llm_analyse.question_label')"
            :hint="$t('llm_analyse.question_hint')"
            persistent-hint
            :placeholder="$t('llm_analyse.question_placeholder')"
            @update:model-value="emitSteps"
          />
          <v-text-field
            v-model="step.llm_question_meta_key"
            class="mt-2"
            outlined
            dense
            :disabled="disabled"
            :label="$t('llm_analyse.question_meta_key_label')"
            :hint="$t('llm_analyse.question_meta_key_hint')"
            persistent-hint
            :placeholder="$t('llm_analyse.question_meta_key_placeholder')"
            @update:model-value="emitSteps"
          />
        </div>

        <div v-if="step.do_llm_prompt_boxes" class="mt-3">
          <p class="text-caption mb-2 font-weight-medium">{{ $t('llm_analyse.prompt_boxes_section_title') }}</p>
          <v-textarea
            v-model="step.llm_prompt_boxes_text"
            outlined
            dense
            rows="4"
            auto-grow
            :disabled="disabled"
            :label="$t('llm_analyse.prompt_boxes_question_label')"
            :hint="$t('llm_analyse.prompt_boxes_question_hint')"
            persistent-hint
            :placeholder="$t('llm_analyse.prompt_boxes_question_placeholder')"
            @update:model-value="emitSteps"
          />
          <search-label-autocomplete
            class="mt-2"
            :label_type="1"
            :field_label="$t('llm_analyse.prompt_boxes_label_field')"
            :placeholder="$t('llm_analyse.prompt_boxes_label_placeholder')"
            :value="step._promptBoxesLabelObj"
            @label-selected="(label) => onPromptBoxesSelect(step, label)"
            @cleared="() => onPromptBoxesCleared(step)"
          />
          <p class="text-caption mt-2 mb-0">{{ $t('llm_analyse.prompt_boxes_hint') }}</p>
        </div>

        <v-switch
          v-model="step.apply_privacy_blur"
          class="mt-3"
          inset
          hide-details
          dense
          color="primary"
          :disabled="disabled"
          :label="$t('settings_project.upload_pipeline_apply_privacy_blur')"
          data-testid="on-upload-step-apply-privacy-blur"
          @update:model-value="emitSteps"
        />
        <p class="text-caption mt-1 mb-0">
          {{ $t('settings_project.upload_pipeline_apply_privacy_blur_hint') }}
        </p>
      </template>
    </div>

    <div class="d-flex flex-wrap" style="gap: 8px;">
      <v-btn
        variant="outlined"
        size="small"
        data-testid="on-upload-add-model"
        :disabled="disabled || modelsLoading || modelSelectItems.length === 0"
        @click="addModelStep"
      >
        {{ $t('settings_project.upload_pipeline_add_model') }}
      </v-btn>
      <v-btn
        v-if="canConfigureLlm"
        variant="outlined"
        size="small"
        data-testid="on-upload-add-llm"
        :disabled="disabled"
        @click="addLlmStep"
      >
        {{ $t('settings_project.upload_pipeline_add_llm') }}
      </v-btn>
      <v-btn
        variant="outlined"
        size="small"
        data-testid="on-upload-add-all-active"
        :disabled="disabled"
        @click="addAllActiveStep"
      >
        {{ $t('settings_project.upload_pipeline_add_all_active') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import SearchLabelAutocomplete from '@/components/SearchLabelAutocomplete.vue';

let nextKey = 1;

function emptyLlmStep() {
  return {
    _key: nextKey++,
    type: 'llm',
    do_find: true,
    do_verify: false,
    do_llm_question: false,
    do_llm_prompt_boxes: false,
    apply_privacy_blur: false,
    find_query: '',
    category_label_id: null,
    llm_question: '',
    llm_question_meta_key: '',
    llm_prompt_boxes_text: '',
    llm_prompt_boxes_label_id: null,
    _subjectChips: [],
    _verifyCategoryObj: null,
    _promptBoxesLabelObj: null,
  };
}

function emptyModelStep(modelId, cropOnLabel, runOnCrop) {
  const crop = cropOnLabel != null && String(cropOnLabel).trim()
    ? String(cropOnLabel).trim()
    : null;
  const wantCrop = crop ? true : !!runOnCrop;
  return {
    _key: nextKey++,
    type: 'model',
    model_id: modelId != null ? Number(modelId) : null,
    run_on_crop: wantCrop,
    crop_on_label: crop,
    _cropLabelObj: crop ? { name: crop } : null,
  };
}

function parseModelMeta(model) {
  if (!model || !model.meta) {
    return {};
  }
  if (typeof model.meta === 'object') {
    return model.meta;
  }
  try {
    return JSON.parse(model.meta) || {};
  } catch (_) {
    return {};
  }
}

function modelPerformancePercent(model, threshold = 50) {
  if (!model || !model.performance) {
    return null;
  }
  try {
    const perf = typeof model.performance === 'string'
      ? JSON.parse(model.performance)
      : model.performance;
    const key = `correct_${Math.min(95, Number(threshold) || 50)}`;
    const score = perf && perf[key];
    if (score === undefined || score === null) {
      return null;
    }
    return Math.round(Number(score) * 100);
  } catch (_) {
    return null;
  }
}

function modelArchitecture(model) {
  const meta = parseModelMeta(model);
  if (model && model.type) {
    return String(model.type);
  }
  if (meta.model_type) {
    return String(meta.model_type);
  }
  if (meta.architecture) {
    return String(meta.architecture);
  }
  return '';
}

function modelSizeLabel(model) {
  const meta = parseModelMeta(model);
  const raw = meta.image_size || meta.size || meta.imgsz || meta.input_size;
  if (raw == null || raw === '') {
    return '';
  }
  if (Array.isArray(raw)) {
    return raw.join('x');
  }
  return String(raw);
}

/** Short date for picker subtitle (models have created_at, no updated_at). */
function modelLastUpdatedLabel(model) {
  if (!model || !model.created_at) {
    return '';
  }
  const s = String(model.created_at);
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function emptyAllActiveStep() {
  return {
    _key: nextKey++,
    type: 'all_active',
  };
}

/** Hydrate API/persisted steps into editable UI steps. */
export function hydratePipelineSteps(rawSteps) {
  const list = Array.isArray(rawSteps) ? rawSteps : [];
  return list.map((s) => {
    const type = (s && s.type) || '';
    if (type === 'model') {
      return emptyModelStep(s.model_id, s.crop_on_label, s.run_on_crop);
    }
    if (type === 'all_active') {
      return emptyAllActiveStep();
    }
    if (type === 'llm') {
      const step = emptyLlmStep();
      step.do_find = !!s.do_find;
      step.do_verify = !!s.do_verify;
      step.do_llm_question = !!s.do_llm_question;
      step.do_llm_prompt_boxes = !!s.do_llm_prompt_boxes;
      if (Object.prototype.hasOwnProperty.call(s, 'apply_privacy_blur')) {
        step.apply_privacy_blur = !!s.apply_privacy_blur;
      } else {
        // Legacy answer-question steps always blurred sensitive regions.
        step.apply_privacy_blur = !!s.do_llm_question;
      }
      step.find_query = s.find_query || '';
      step.category_label_id = s.category_label_id != null ? Number(s.category_label_id) : null;
      step.llm_question = s.llm_question || '';
      step.llm_question_meta_key = s.llm_question_meta_key || '';
      step.llm_prompt_boxes_text = s.llm_prompt_boxes_text || '';
      step.llm_prompt_boxes_label_id = s.llm_prompt_boxes_label_id != null
        ? Number(s.llm_prompt_boxes_label_id)
        : null;
      const fq = (step.find_query || '').trim();
      step._subjectChips = fq ? fq.split(',').map((x) => x.trim()).filter(Boolean) : [];
      step._verifyCategoryObj = step.category_label_id != null
        ? { id: step.category_label_id }
        : null;
      step._promptBoxesLabelObj = step.llm_prompt_boxes_label_id != null
        ? { id: step.llm_prompt_boxes_label_id }
        : null;
      return step;
    }
    return null;
  }).filter(Boolean);
}

/** Serialize UI steps for the on_upload_pipeline_json setting. */
export function serializePipelineSteps(steps) {
  return (steps || []).map((s) => {
    if (s.type === 'model') {
      const out = { type: 'model', model_id: Number(s.model_id) };
      const crop = (s.crop_on_label || '').trim();
      if (s.run_on_crop) {
        // Keep UI intent across v-model round-trips even before a label is chosen.
        out.run_on_crop = true;
      }
      if (s.run_on_crop && crop) {
        out.crop_on_label = crop;
      }
      return out;
    }
    if (s.type === 'all_active') {
      return { type: 'all_active' };
    }
    if (s.type === 'llm') {
      return {
        type: 'llm',
        do_find: !!s.do_find,
        do_verify: !!s.do_verify,
        do_llm_question: !!s.do_llm_question,
        do_llm_prompt_boxes: !!s.do_llm_prompt_boxes,
        apply_privacy_blur: !!s.apply_privacy_blur,
        find_query: subjectsText(s),
        category_label_id: s.category_label_id != null ? Number(s.category_label_id) : null,
        llm_question: (s.llm_question || '').trim(),
        llm_question_meta_key: (s.llm_question_meta_key || '').trim(),
        llm_prompt_boxes_text: (s.llm_prompt_boxes_text || '').trim(),
        llm_prompt_boxes_label_id: s.llm_prompt_boxes_label_id != null
          ? Number(s.llm_prompt_boxes_label_id)
          : null,
      };
    }
    return null;
  }).filter(Boolean);
}

function subjectsText(step) {
  const parts = (step._subjectChips || [])
    .map((x) => (typeof x === 'string' ? x.trim() : String(x).trim()))
    .filter(Boolean);
  return parts.join(', ');
}

export default {
  name: 'OnUploadPipelineEditor',
  components: { SearchLabelAutocomplete },
  props: {
    modelValue: { type: Array, default: () => [] },
    models: { type: Array, default: () => [] },
    modelsLoading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    canConfigureLlm: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      steps: [],
      dragFrom: null,
      syncing: false,
    };
  },
  computed: {
    modelSelectItems() {
      const items = [];
      (this.models || []).forEach((m) => {
        if (!m || m.archived) {
          return;
        }
        const name = m.name || (`#${m.id}`);
        const arch = modelArchitecture(m);
        const size = modelSizeLabel(m);
        const perf = modelPerformancePercent(m);
        const lastUpdated = modelLastUpdatedLabel(m);
        const metaParts = [];
        if (arch) {
          metaParts.push(arch);
        }
        if (size) {
          metaParts.push(size);
        }
        if (perf != null) {
          metaParts.push(`${perf}%`);
        }
        if (lastUpdated) {
          metaParts.push(lastUpdated);
        }
        if (!m.active) {
          metaParts.push(this.$t('settings_project.upload_tasks_model_inactive'));
        }
        items.push({
          value: m.id,
          title: name,
          subtitle: metaParts.length ? metaParts.join(' · ') : '',
        });
      });
      return items;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler(val) {
        if (this.syncing) {
          return;
        }
        this.steps = hydratePipelineSteps(val);
      },
    },
  },
  methods: {
    stepTypeLabel(type) {
      if (type === 'model') {
        return this.$t('settings_project.upload_pipeline_type_model');
      }
      if (type === 'llm') {
        return this.$t('settings_project.upload_pipeline_type_llm');
      }
      if (type === 'all_active') {
        return this.$t('settings_project.upload_pipeline_type_all_active');
      }
      return type;
    },
    emitSteps() {
      this.syncing = true;
      this.$emit('update:modelValue', serializePipelineSteps(this.steps));
      this.$nextTick(() => {
        this.syncing = false;
      });
    },
    addModelStep() {
      const first = this.modelSelectItems[0];
      this.steps.push(emptyModelStep(first ? first.value : null));
      this.emitSteps();
    },
    addLlmStep() {
      this.steps.push(emptyLlmStep());
      this.emitSteps();
    },
    addAllActiveStep() {
      this.steps.push(emptyAllActiveStep());
      this.emitSteps();
    },
    removeStep(index) {
      this.steps.splice(index, 1);
      this.emitSteps();
    },
    onDragStart(index, event) {
      this.dragFrom = index;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
      }
    },
    onDragOver(index) {
      if (this.dragFrom === null || this.dragFrom === index) {
        return;
      }
      const from = this.dragFrom;
      const moved = this.steps.splice(from, 1)[0];
      this.steps.splice(index, 0, moved);
      this.dragFrom = index;
      this.emitSteps();
    },
    onDrop(index) {
      this.onDragOver(index);
      this.dragFrom = null;
    },
    onDragEnd() {
      this.dragFrom = null;
    },
    onLlmTaskToggle(step, key, enabled) {
      const keys = ['do_find', 'do_verify', 'do_llm_question', 'do_llm_prompt_boxes'];
      if (enabled) {
        keys.forEach((k) => {
          step[k] = k === key;
        });
        if (key !== 'do_find') {
          step._subjectChips = [];
          step.find_query = '';
        }
        if (key !== 'do_verify') {
          step._verifyCategoryObj = null;
          step.category_label_id = null;
        }
        if (key !== 'do_llm_question') {
          step.llm_question = '';
          step.llm_question_meta_key = '';
        } else {
          // Answer-question is typically a sensitive-info check — enable blur by default.
          step.apply_privacy_blur = true;
        }
        if (key !== 'do_llm_prompt_boxes') {
          step.llm_prompt_boxes_text = '';
          step.llm_prompt_boxes_label_id = null;
          step._promptBoxesLabelObj = null;
        }
      }
      this.emitSteps();
    },
    syncFindQuery(step) {
      step.find_query = subjectsText(step);
      this.emitSteps();
    },
    onVerifySelect(step, label) {
      if (!label || label.id == null) {
        step.category_label_id = null;
        step._verifyCategoryObj = null;
      } else {
        step.category_label_id = label.id;
        step._verifyCategoryObj = label;
      }
      this.emitSteps();
    },
    onVerifyCleared(step) {
      step.category_label_id = null;
      step._verifyCategoryObj = null;
      this.emitSteps();
    },
    onPromptBoxesSelect(step, label) {
      if (!label || label.id == null) {
        step.llm_prompt_boxes_label_id = null;
        step._promptBoxesLabelObj = null;
      } else {
        step.llm_prompt_boxes_label_id = label.id;
        step._promptBoxesLabelObj = label;
      }
      this.emitSteps();
    },
    onPromptBoxesCleared(step) {
      step.llm_prompt_boxes_label_id = null;
      step._promptBoxesLabelObj = null;
      this.emitSteps();
    },
    onRunOnCropToggle(step, enabled) {
      step.run_on_crop = !!enabled;
      if (!enabled) {
        step.crop_on_label = null;
        step._cropLabelObj = null;
        this.emitSteps();
        return;
      }
      // Defer parent sync until a crop label is chosen so the switch stays on
      // during the intermediate UI state (serialize would otherwise round-trip).
      if ((step.crop_on_label || '').trim()) {
        this.emitSteps();
      }
    },
    onCropLabelSelect(step, label) {
      if (!label || (!label.name && label.id == null)) {
        step.crop_on_label = null;
        step._cropLabelObj = null;
      } else {
        step.crop_on_label = label.name || null;
        step._cropLabelObj = label;
        step.run_on_crop = true;
      }
      this.emitSteps();
    },
    onCropLabelCleared(step) {
      step.crop_on_label = null;
      step._cropLabelObj = null;
      this.emitSteps();
    },
    /** Validate LLM steps; returns error i18n key or null. */
    validate() {
      for (const step of this.steps) {
        if (step.type === 'model' && (step.model_id == null || step.model_id === '')) {
          return 'settings_project.upload_pipeline_need_model';
        }
        if (step.type === 'model' && step.run_on_crop && !(step.crop_on_label || '').trim()) {
          return 'settings_project.upload_pipeline_need_crop_label';
        }
        if (step.type !== 'llm') {
          continue;
        }
        if (!step.do_find && !step.do_verify && !step.do_llm_question && !step.do_llm_prompt_boxes) {
          return 'llm_analyse.need_step';
        }
        if (step.do_find && !subjectsText(step)) {
          return 'llm_analyse.need_subjects';
        }
        if (step.do_verify && (step.category_label_id == null || step.category_label_id === '')) {
          return 'llm_analyse.need_verify_category';
        }
        if (step.do_llm_question) {
          const q = (step.llm_question || '').trim();
          const k = (step.llm_question_meta_key || '').trim();
          if (!q || !k) {
            return 'llm_analyse.need_question';
          }
        }
        if (step.do_llm_prompt_boxes) {
          const t = (step.llm_prompt_boxes_text || '').trim();
          if (!t) {
            return 'llm_analyse.need_prompt_boxes_text';
          }
          if (step.llm_prompt_boxes_label_id == null || step.llm_prompt_boxes_label_id === '') {
            return 'llm_analyse.need_prompt_boxes_label';
          }
        }
      }
      return null;
    },
  },
};
</script>

<style scoped>
.pipeline-step {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
}
.pipeline-step--dragging {
  opacity: 0.7;
  border-color: rgb(var(--v-theme-primary));
}
.pipeline-drag-handle {
  cursor: grab;
  display: inline-flex;
  padding: 4px;
  color: rgba(0, 0, 0, 0.45);
}
.pipeline-drag-handle:active {
  cursor: grabbing;
}
</style>
